/**
 * NotificationContext - Manages browser notifications globally
 *
 * Features:
 * - Permission status tracking
 * - App visibility tracking (document.visibilityState + document.hasFocus())
 * - Integration with SettingsContext for user preferences
 * - Notification lifecycle management through notificationService
 *
 * Key behavior:
 * - App visibility (foreground) is tracked and exposed for callers
 * - Per-room notification suppression is handled by useNotificationEvents
 * - Respects browserNotifications setting from SettingsContext
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import notificationService from '../services/notificationService';
import { useSettings } from './SettingsContext';
import logger from '../utils/logger';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { settings } = useSettings();

  // Permission status from notification service
  const [permissionStatus, setPermissionStatus] = useState(() =>
    notificationService.checkPermission(),
  );

  // App visibility state - true when app is in foreground (visible AND focused)
  const [isAppVisible, setIsAppVisible] = useState(true);

  // Permission banner state - shows when notification needed but permission is 'default'
  const [showPermissionBanner, setShowPermissionBanner] = useState(false);
  const [bannerDismissedThisSession, setBannerDismissedThisSession] = useState(false);

  /**
   * Track visibility and focus state
   * App is considered "in foreground" when BOTH:
   * - document.visibilityState === 'visible'
   * - document.hasFocus() === true
   */
  useEffect(() => {
    const updateVisibility = () => {
      const isVisible = document.visibilityState === 'visible';
      const isFocused = document.hasFocus();
      const isInForeground = isVisible && isFocused;

      setIsAppVisible(isInForeground);
      logger.debug(`[NotificationContext] Visibility updated: visible=${isVisible}, focused=${isFocused}, inForeground=${isInForeground}`);
    };

    // Listen for visibility and focus changes
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', updateVisibility);
    window.addEventListener('blur', updateVisibility);

    // Initial check
    updateVisibility();

    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', updateVisibility);
      window.removeEventListener('blur', updateVisibility);
    };
  }, []);

  /**
   * Request notification permission from the browser
   * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>} Permission result
   */
  const requestPermission = useCallback(async () => {
    logger.debug('[NotificationContext] requestPermission called');
    const result = await notificationService.requestPermission();
    logger.debug(`[NotificationContext] requestPermission result: ${result}`);
    setPermissionStatus(result);
    // Hide banner if permission was granted
    if (result === 'granted') {
      setShowPermissionBanner(false);
    }
    return result;
  }, []);

  /**
   * Dismiss the permission banner for this session
   */
  const dismissPermissionBanner = useCallback(() => {
    logger.debug('[NotificationContext] Permission banner dismissed');
    setShowPermissionBanner(false);
    setBannerDismissedThisSession(true);
  }, []);

  /**
   * Show a browser notification if conditions are met
   *
   * Notifications are shown when:
   * 1. Browser notifications are enabled in settings
   * 2. Browser permission is granted (or triggers permission banner if 'default')
   *
   * Note: Per-room visibility suppression (don't notify for the room being viewed)
   * is handled by useNotificationEvents, not here.
   *
   * @param {Object} options - Notification options (passed to notificationService.showNotification)
   * @param {string} options.eventId - Unique identifier for the event
   * @param {string} options.roomId - Room ID for grouping notifications
   * @param {string} options.title - Notification title
   * @param {string} [options.body] - Notification body text
   * @param {string} [options.icon] - URL for notification icon
   * @param {string} [options.tag] - Tag for notification grouping
   * @param {Function} [options.onClick] - Callback when notification is clicked
   * @returns {string|null} eventId if notification was created, null otherwise
   */
  const showNotification = useCallback((options) => {
    // Don't show if notifications are disabled in settings
    if (!settings.browserNotifications) {
      logger.debug('[NotificationContext] Skipping notification - disabled in settings');
      return null;
    }

    // If permission is 'default', show the permission banner instead
    if (permissionStatus === 'default') {
      logger.debug('[NotificationContext] Permission is default - triggering banner');
      if (!bannerDismissedThisSession) {
        setShowPermissionBanner(true);
      }
      return null;
    }

    return notificationService.showNotification(options);
  }, [settings.browserNotifications, permissionStatus, bannerDismissedThisSession]);

  /**
   * Cancel a specific notification by event ID
   * @param {string} eventId - The event ID of the notification to cancel
   */
  const cancelNotification = useCallback((eventId) => {
    notificationService.cancelNotification(eventId);
  }, []);

  /**
   * Cancel all notifications for a specific room
   * @param {string} roomId - The room ID whose notifications should be cancelled
   * @returns {number} Number of notifications cancelled
   */
  const cancelRoomNotifications = useCallback((roomId) => {
    return notificationService.cancelRoomNotifications(roomId);
  }, []);

  /**
   * Cancel all active notifications
   * @returns {number} Number of notifications cancelled
   */
  const cancelAllNotifications = useCallback(() => {
    return notificationService.cancelAllNotifications();
  }, []);

  const value = {
    // State
    permissionStatus,
    isAppVisible,
    isSupported: notificationService.isSupported,
    showPermissionBanner,

    // Permission management
    requestPermission,
    dismissPermissionBanner,

    // Notification management
    showNotification,
    cancelNotification,
    cancelRoomNotifications,
    cancelAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access notification context
 * @returns {Object} Notification context value
 * @throws {Error} If used outside NotificationProvider
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
